import React from 'react';
import {
  Box,
  Typography,
  Slider,
  TextField,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import Insights from '@mui/icons-material/Insights';
import SettingsIcon from '@mui/icons-material/Settings';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { getEffectiveTierUsers } from '../../../../utils/tierUsers';

/**
 * AI Conversation Intelligence component for the pricing calculator
 * @param {Object} props - Component props
 * @param {number} props.value - Current value (minutes)
 * @param {Function} props.setValue - Function to update value
 * @param {number} props.min - Minimum value
 * @param {number} props.max - Maximum value
 * @param {string} props.description - Product description
 * @param {number} props.price - Calculated price
 * @param {boolean} props.hasCustomSettings - Whether custom settings are enabled
 * @param {Function} props.onOpenSettings - Function to open settings modal
 * @param {string} props.currency - Currency symbol
 * @param {string} props.id - Product ID
 * @param {Function} props.toggleProduct - Function to toggle product selection
 */
const AIConversation = ({
  value,
  setValue,
  min,
  max,
  description,
  price,
  hasCustomSettings,
  onOpenSettings,
  currency,
  id,
  toggleProduct
}) => {
  const { t } = useTranslation();
  const { veryCustomProductDiscounts, packageTier, businessPhoneUsers, fmcUsers, businessPhoneType } = useCalculator();

  // Check if this product is included in the current tier (Pro only —
  // Corporate includes Eniq AI Standard instead)
  const isIncludedInTier = packageTier === 'pro';

  // For Basic/Advanced/Corporate tier, AI users = BP/FMC users (per-user pricing)
  const isBasicTier = packageTier === 'basic' || packageTier === 'advanced' || packageTier === 'corporate';

  // Calculate effective AI users based on BP/FMC selection
  const effectiveAiUsers = isBasicTier
    ? getEffectiveTierUsers({ businessPhoneUsers, fmcUsers, businessPhoneType, packageTier })
    : value;

  // Check if there are any custom settings (including very custom discounts)
  const hasAnyCustomSettings = hasCustomSettings || (veryCustomProductDiscounts['ai_conversation'] && veryCustomProductDiscounts['ai_conversation'] > 0);

  return (
    <Box sx={{ 
      borderRadius: 2,
    }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Insights sx={{ color: '#333', fontSize: 20, mr: 1 }} />
          <Typography sx={{ fontWeight: 500 }}>
            {t('calculator.aiConversation', 'AI Conversation Intelligence')}
          </Typography>
          {isIncludedInTier && (
            <Chip
              label={t('calculator.included', 'INCLUDED')}
              size="small"
              sx={{
                ml: 1,
                bgcolor: 'rgba(0, 200, 83, 0.1)',
                color: '#00C853',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: 20,
              }}
            />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {/* Hide settings and close button when included in tier */}
          {!isIncludedInTier && onOpenSettings && (
            <Tooltip title="AI Settings">
              <IconButton
                size="small"
                onClick={onOpenSettings}
                sx={{ color: hasAnyCustomSettings ? '#00C853' : '#757575', mr: 1, '&:hover': { color: '#00C853' } }}
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isIncludedInTier && (
            <IconButton
              size="small"
              onClick={() => toggleProduct(id)}
              sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>
      
      {/* Controls - hide when included in tier (Pro/Corporate) */}
      {!isIncludedInTier && (
        <>
          {/* For Basic tier - show locked users count tied to BP/FMC */}
          {isBasicTier ? (
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
                {t('calculator.aiUsersLabel', 'Number of users')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={effectiveAiUsers}
                    min={3}
                    max={100}
                    disabled
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={effectiveAiUsers}
                  disabled
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
              {/* Info message explaining the locked state */}
              <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption">
                  {t('calculator.aiUsersLinkedInfo', 'AI users are linked to Business Phone/FMC users count')}
                </Typography>
              </Box>
            </>
          ) : (
            /* Legacy mode - editable minutes (when no package tier) */
            <>
              <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2, mb: 0.5 }}>
                {t('calculator.minutesToAnalyze', 'Number of minutes to analyze per month')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ flex: 1, mr: 2 }}>
                  <Slider
                    value={value}
                    min={min}
                    max={max}
                    onChange={(_, v) => setValue(Number(v))}
                    sx={{
                      color: '#00C853',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#00C853',
                      }
                    }}
                  />
                </Box>
                <TextField
                  value={value}
                  onChange={e => {
                    const newValue = e.target.value === '' ? '' : Number(e.target.value);
                    setValue(newValue);
                  }}
                  onBlur={() => {
                    if (value === '' || value < min) {
                      setValue(min);
                    }
                  }}
                  type="number"
                  variant="outlined"
                  size="small"
                  sx={{ width: 90 }}
                  inputProps={{ inputMode: 'numeric' }}
                />
              </Box>
            </>
          )}
        </>
      )}

      {/* Show included message for Pro/Corporate tiers */}
      {isIncludedInTier && (
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          {t('calculator.includedInTier', 'Included in your {{tier}} plan', { tier: packageTier.charAt(0).toUpperCase() + packageTier.slice(1) })}
        </Typography>
      )}
    </Box>
  );
};

export default React.memo(AIConversation);
